<template>
  <Teleport to="body">
    <Transition
      enter-active-class="tw:transition-opacity tw:duration-150"
      enter-from-class="tw:opacity-0"
      leave-active-class="tw:transition-opacity tw:duration-150"
      leave-to-class="tw:opacity-0"
    >
      <div
        v-if="isOpen"
        class="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:bg-[rgba(0,0,0,0.64)] tw:z-[999] tw:flex tw:justify-center tw:items-center"
        @click.self="$emit('close')"
      >
        <div
          class="tw:relative tw:flex tw:flex-col tw:w-[50rem] tw:bg-white tw:rounded-[0.25rem]"
          :class="{
            'tw:h-[31.25rem]': hasRecentSearch,
          }"
        >
          <SearchBarInput :search-query="searchQuery" @input="handleOnSearchQuery" />

          <SearchBarGroups
            :has-no-results="hasNoResults"
            :groups="groups"
            :selected-group="selectedGroup"
            :hide-counter="searchQuery.length === 0 || isLoading"
            @group-by="handleGroupBy"
          />

          <template v-if="hasRecentSearch">
            <div class="tw:flex tw:flex-col tw:flex-1 tw:overflow-hidden">
              <div class="tw:flex-1 tw:overflow-auto tw:h-full tw:w-full">
                <div
                  v-if="isLoading"
                  class="tw:flex tw:flex-col tw:gap-5 tw:justify-center tw:items-center tw:h-full tw:w-full"
                >
                  <EdroneLoading />
                  <p class="tw:body-1 tw:text-default-700">{{ trans._menu.searchBar.searching }}</p>
                </div>

                <SearchBarEmptyState
                  v-else-if="hasNoResults && searchQuery.length"
                  :group-by="selectedGroup"
                  :query="searchQuery"
                />

                <div v-else ref="items" class="tw:flex-1 tw:overflow-auto tw:h-full tw:w-full" @scroll="handleOnScroll">
                  <slot />

                  <div v-if="isLoadingNextPage" class="tw:my-4">
                    <EdroneLoading />
                  </div>
                </div>
              </div>
              <SearchBarBottom />
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import SearchBarInput from "./SearchBarInput.vue";
import SearchBarEmptyState from "./SearchBarEmptyState.vue";
import { useTemplateRef } from "vue";
import SearchBarBottom from "./SearchBarBottom.vue";
import { sendCustomEventToGA } from "vue-app/plugins/edroneGtag";
import SearchBarGroups from "vue-app/components/SearchBar/SearchBarGroups.vue";
import { SEARCH_BAR_GROUP } from "vue-app/components/SearchBar/SearchBar.constants";

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  isLoadingNextPage: boolean;
  searchQuery: string;
  hasNoResults: boolean;
  hasRecentSearch: boolean;
  selectedGroup: SEARCH_BAR_GROUP;
  groups: { label: string; group: SEARCH_BAR_GROUP; count: number; disabled: boolean }[];
}

interface Emits {
  input: [query: string];
  close: [];
  infinityScroll: [];
  groupBy: [group: SEARCH_BAR_GROUP];
}

const emit = defineEmits<Emits>();
defineProps<Props>();
const itemsRef = useTemplateRef("items");

const handleOnScroll = () => {
  if (!itemsRef.value) return;

  const { value } = itemsRef;

  const isAtBottom = value.scrollTop + value.clientHeight >= value.scrollHeight - 50;

  if (isAtBottom) {
    emit("infinityScroll");
  }
};

const handleOnSearchQuery = (query: string) => {
  emit("input", query);
  sendCustomEventToGA("search_bar_input_query", { query });
};

const handleGroupBy = (group: SEARCH_BAR_GROUP) => {
  emit("groupBy", group);
  itemsRef.value?.scrollTo({ top: 0 });
};
</script>
